import React, { useCallback, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map } from 'immutable'
import _trim from 'lodash/trim'
import FormsyForm from 'formsy-react'
import { TextInput } from '../FormInput.v2/index'
import Button, { TYPES, SIZES } from '../Button/index'
import ArrowUp from '../Icon/icons/ArrowUp/index'
import VoiceMode from '../Icon/icons/VoiceMode/index'
import { ICON_COLORS } from '../Icon/index'
import HistoryContext from '../Router/HistoryContext'
import { historyRedirect } from '../../services/navigation/index'
import { URL_AI_CHAT } from '../../services/url/constants'
import {
  AI_CONVERSATION_STARTED_GA4_EVENT,
  UI_ELEMENT,
  UI_LOCATION,
} from '../../services/event-tracking/index'
import { AI_PROMPT_FIELD, MEMBER_HOME_FIRST_TOUCH_AI_INPUT_SEED_PROMPT_KEYS } from './utils'
import MemberHomeFirstTouchAiPrompts, {
  MEMBER_HOME_FIRST_TOUCH_AI_PROMPTS_LAYOUT_STRETCH,
} from './MemberHomeFirstTouchAiPrompts'

/** Text field, send, and starter suggestions.
 * Existing chats: `MemberHomeFirstTouchAiConversations`. */
function MemberHomeFirstTouchAiInput ({
  auth = Map(),
  staticText = Map(),
  language,
  oraclePersonalityId,
  oracleVoiceAccess,
  aiValue,
  onAiPromptChange,
  colorSchemePrefix,
  showSeedPrompts = true,
  setAiGa4Event,
  sendOptimizelyFeatureEvent,
}) {
  const history = useContext(HistoryContext)

  const hasPrompt = useMemo(() => _trim(aiValue).length > 0, [aiValue])

  const handleSendVoiceModeGa4Event = useCallback(() => {
    setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
      ui_element: UI_ELEMENT.FIRST_TOUCH,
      ai_persona: oraclePersonalityId,
    }))
  }, [oraclePersonalityId, setAiGa4Event])

  const handleAiFormSubmit = useCallback((formData) => {
    const raw = formData[AI_PROMPT_FIELD]
    const userPrompt = raw != null ? _trim(String(raw)) : ''
    if (!userPrompt || !history) {
      return
    }
    sendOptimizelyFeatureEvent({ eventName: 'ai_message_sent' })
    historyRedirect({
      history,
      url: URL_AI_CHAT,
      query: {
        prompt: userPrompt,
        oraclePersonalityId,
        // AI-1328: the chat route reports this send on arrival, and only when
        // the deep link names its surface.
        uiLocation: UI_LOCATION.MEMBER_HOME,
        uiElement: UI_ELEMENT.TEXT_FIELD,
      },
      auth,
      language,
    })
  }, [auth, history, language, oraclePersonalityId, sendOptimizelyFeatureEvent])

  return (
    <div className="member-home-first-touch__ai">
      <div className={`member-home-first-touch__ai-label member-home-first-touch__ai-label--${colorSchemePrefix}`}>
        {staticText.get('getPersonalizedRecommendation')}
      </div>
      <FormsyForm
        className="member-home-first-touch__ai-form"
        onValidSubmit={handleAiFormSubmit}
      >
        <div className={`member-home-first-touch__ai-form-content member-home-first-touch__ai-form-content--${colorSchemePrefix}`}>
          <TextInput
            className="member-home-first-touch__ai-input"
            name={AI_PROMPT_FIELD}
            label={staticText.get('whatWouldYouLikeToWatch')}
            placeholder={staticText.get('whatWouldYouLikeToWatch')}
            value={aiValue}
            onChange={onAiPromptChange}
          />
          <Button
            type={TYPES.DEFAULT}
            size={SIZES.XSMALL}
            submit={hasPrompt}
            onClick={!hasPrompt ? handleSendVoiceModeGa4Event : null}
            className="member-home-first-touch__submit-button"
            url={!hasPrompt ? URL_AI_CHAT : undefined}
            query={{
              oraclePersonalityId,
              ...(!hasPrompt && oracleVoiceAccess ? { voiceMode: true } : {}),
            }}
            aria-label={staticText.get('submitButtonAriaLabel')}
          >
            {
              !hasPrompt && oracleVoiceAccess
                ? <VoiceMode size={22} color={ICON_COLORS.WHITE} />
                : <ArrowUp size={22} color={ICON_COLORS.WHITE} />
            }
          </Button>
        </div>
      </FormsyForm>
      {showSeedPrompts && (
        <MemberHomeFirstTouchAiPrompts
          auth={auth}
          staticText={staticText}
          oraclePersonalityId={oraclePersonalityId}
          promptKeys={MEMBER_HOME_FIRST_TOUCH_AI_INPUT_SEED_PROMPT_KEYS}
          layout={MEMBER_HOME_FIRST_TOUCH_AI_PROMPTS_LAYOUT_STRETCH}
        />
      )}
    </div>
  )
}

MemberHomeFirstTouchAiInput.propTypes = {
  auth: ImmutablePropTypes.map,
  staticText: ImmutablePropTypes.map.isRequired,
  language: PropTypes.string.isRequired,
  oraclePersonalityId: PropTypes.string.isRequired,
  oracleVoiceAccess: PropTypes.bool,
  aiValue: PropTypes.string.isRequired,
  onAiPromptChange: PropTypes.func.isRequired,
  colorSchemePrefix: PropTypes.string.isRequired,
  showSeedPrompts: PropTypes.bool,
  setAiGa4Event: PropTypes.func.isRequired,
}

MemberHomeFirstTouchAiInput.defaultProps = {
  auth: Map(),
  oracleVoiceAccess: false,
  showSeedPrompts: true,
}

export default MemberHomeFirstTouchAiInput
